// src/services/useBulkMessageStore.js
/**
 * Zustand store para gerenciar processos de envio em massa.
 *
 * Responsável por:
 * - Manter estado dos processos ativos/recentes
 * - Polling automático para atualizar progresso
 * - Adicionar/remover processos
 */

import { create } from "zustand";
import { api } from "./api";

// Intervalo de polling em ms
const POLLING_INTERVAL = 2000;

// Mensagens amigáveis para exibir durante o envio
export const friendlyMessages = [
  "Estamos enviando em ritmo seguro para proteger sua conta.",
  "Controlando o volume para reduzir bloqueios e limites da API.",
  "Aguarde, os envios continuam acontecendo em segundo plano.",
  "O CRM pode ser usado normalmente enquanto os disparos continuam.",
  "Estamos respeitando intervalos para evitar comportamento suspeito.",
];

// Retorna mensagem aleatória
export const getRandomFriendlyMessage = () => {
  return friendlyMessages[Math.floor(Math.random() * friendlyMessages.length)];
};

const useBulkMessageStore = create((set, get) => ({
  // Estado
  processes: [],
  isManagerOpen: false,
  isPolling: false,
  pollingIntervalId: null,
  lastError: null,

  // Ações síncronas
  setManagerOpen: (isOpen) => set({ isManagerOpen: isOpen }),

  toggleManager: () => set((state) => ({ isManagerOpen: !state.isManagerOpen })),

  addProcess: (process) =>
    set((state) => {
      // Evita duplicatas
      const exists = state.processes.some((p) => p.id === process.id);
      if (exists) return state;

      return {
        processes: [process, ...state.processes],
        isManagerOpen: true, // Abre automaticamente ao adicionar processo
      };
    }),

  updateProcess: (processId, updates) =>
    set((state) => ({
      processes: state.processes.map((p) =>
        p.id === processId ? { ...p, ...updates } : p
      ),
    })),

  removeProcess: (processId) =>
    set((state) => ({
      processes: state.processes.filter((p) => p.id !== processId),
    })),

  clearCompletedProcesses: () =>
    set((state) => ({
      processes: state.processes.filter(
        (p) => !["COMPLETED", "COMPLETED_WITH_ERRORS", "FAILED", "CANCELLED"].includes(p.status)
      ),
    })),

  // Ações assíncronas

  /**
   * Inicia um novo processo de envio em massa.
   * Chama a API e adiciona o processo ao store.
   */
  startBulkMessage: async (payload) => {
    try {
      const response = await api.post("/api/bulk-message/start", payload);

      if (response.data?.process_id) {
        const newProcess = {
          id: response.data.process_id,
          status: "PENDING",
          total_messages: response.data.total_messages || payload.recipients?.length || 0,
          sent_count: 0,
          failed_count: 0,
          pending_count: response.data.total_messages || payload.recipients?.length || 0,
          progress_percent: 0,
          channel: payload.channel || "WHATSAPP",
          created_at: new Date().toISOString(),
        };

        get().addProcess(newProcess);
        get().startPolling();

        return { success: true, process: newProcess };
      }

      return { success: false, error: response.data?.error || "Erro desconhecido" };
    } catch (err) {
      console.error("Erro ao iniciar envio em massa:", err);
      set({ lastError: err.response?.data?.error || err.message });
      return { success: false, error: err.response?.data?.error || err.message };
    }
  },

  /**
   * Busca status atualizado dos processos ativos.
   */
  fetchProcesses: async () => {
    try {
      // Buscar apenas processos não finalizados ou recentes
      const response = await api.get("/api/bulk-message/processes", {
        params: { limit: 10 },
      });

      if (response.data?.processes) {
        const serverProcesses = response.data.processes;

        set((state) => {
          // Atualiza processos existentes e adiciona novos
          const updatedProcesses = state.processes.map((localProcess) => {
            const serverProcess = serverProcesses.find((sp) => sp.id === localProcess.id);
            return serverProcess ? { ...localProcess, ...serverProcess } : localProcess;
          });

          // Adiciona processos do servidor que não estão localmente
          const localIds = new Set(state.processes.map((p) => p.id));
          const newProcesses = serverProcesses.filter((sp) => !localIds.has(sp.id));

          return {
            processes: [...newProcesses, ...updatedProcesses],
          };
        });
      }
    } catch (err) {
      console.error("Erro ao buscar processos:", err);
      set({ lastError: err.response?.data?.error || err.message });
    }
  },

  /**
   * Busca detalhes de um processo específico.
   */
  fetchProcessDetails: async (processId) => {
    try {
      const response = await api.get(`/api/bulk-message/processes/${processId}`);

      if (response.data) {
        get().updateProcess(processId, response.data);
        return response.data;
      }
    } catch (err) {
      console.error("Erro ao buscar detalhes do processo:", err);
    }
    return null;
  },

  /**
   * Busca erros de um processo.
   */
  fetchProcessErrors: async (processId) => {
    try {
      const response = await api.get(`/api/bulk-message/processes/${processId}/errors`);
      return response.data?.errors || [];
    } catch (err) {
      console.error("Erro ao buscar erros do processo:", err);
      return [];
    }
  },

  /**
   * Cancela um processo em andamento.
   */
  cancelProcess: async (processId) => {
    try {
      const response = await api.post(`/api/bulk-message/processes/${processId}/cancel`);

      if (response.data?.success) {
        get().updateProcess(processId, { status: "CANCELLED" });
        return { success: true };
      }

      return { success: false, error: response.data?.error };
    } catch (err) {
      console.error("Erro ao cancelar processo:", err);
      return { success: false, error: err.response?.data?.error || err.message };
    }
  },

  /**
   * Oculta um processo concluído.
   * Remove localmente mesmo se a API falhar.
   */
  hideProcess: async (processId) => {
    // Remove localmente primeiro para feedback imediato
    get().removeProcess(processId);

    try {
      await api.post(`/api/bulk-message/processes/${processId}/hide`);
      return { success: true };
    } catch (err) {
      // Já removemos localmente, apenas loga o erro
      console.error("Erro ao ocultar processo no servidor:", err);
      return { success: true }; // Retorna sucesso pois removemos localmente
    }
  },

  /**
   * Inicia polling para atualizar processos ativos.
   */
  startPolling: () => {
    const { isPolling, processes } = get();

    // Já está fazendo polling
    if (isPolling) return;

    // Não há processos ativos para monitorar
    const hasActiveProcesses = processes.some(
      (p) => p.status === "PENDING" || p.status === "PROCESSING"
    );

    if (!hasActiveProcesses) return;

    const intervalId = setInterval(() => {
      const currentProcesses = get().processes;
      const stillActive = currentProcesses.some(
        (p) => p.status === "PENDING" || p.status === "PROCESSING"
      );

      if (stillActive) {
        get().fetchProcesses();
      } else {
        // Não há mais processos ativos, para o polling
        get().stopPolling();
      }
    }, POLLING_INTERVAL);

    set({ isPolling: true, pollingIntervalId: intervalId });
  },

  /**
   * Para o polling.
   */
  stopPolling: () => {
    const { pollingIntervalId } = get();

    if (pollingIntervalId) {
      clearInterval(pollingIntervalId);
    }

    set({ isPolling: false, pollingIntervalId: null });
  },

  /**
   * Limpa o store (ao desmontar componente principal).
   */
  cleanup: () => {
    get().stopPolling();
    set({ processes: [], isManagerOpen: false, lastError: null });
  },

  // Getters computados
  hasActiveProcesses: () => {
    return get().processes.some(
      (p) => p.status === "PENDING" || p.status === "PROCESSING"
    );
  },

  getProcessById: (processId) => {
    return get().processes.find((p) => p.id === processId);
  },

  getActiveProcessesCount: () => {
    return get().processes.filter(
      (p) => p.status === "PENDING" || p.status === "PROCESSING"
    ).length;
  },
}));

export default useBulkMessageStore;
